import { useState, useEffect, useRef, Fragment } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";
import ListingComments from "../components/ListingComments";
import { getMortgageConfig } from "../utils/mortgageRatesApi";
import { generateCashFlowProjection } from "../utils/irrCalculator";
import { calculateClosingCosts } from "../utils/closingCostsCalculator";
import CurrencyInput from "../components/CurrencyInput";

export default function MISPage() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states (same as kanban)
  const [searchAddress, setSearchAddress] = useState("");
  const [bedsMin, setBedsMin] = useState("");
  const [bedsMax, setBedsMax] = useState("");
  const [bathsMin, setBathsMin] = useState("");
  const [bathsMax, setBathsMax] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [capRateMin, setCapRateMin] = useState("");
  const [capRateMax, setCapRateMax] = useState("");
  const [daysOnMarketMax, setDaysOnMarketMax] = useState("");
  const [propertyType, setPropertyType] = useState("all");
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [defaultNeighborhoods, setDefaultNeighborhoods] = useState([]);
  const [neighborhoodsLoaded, setNeighborhoodsLoaded] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [totalCount, setTotalCount] = useState(0);

  // Sort state
  const [sortField, setSortField] = useState("listed_at");
  const [sortDir, setSortDir] = useState("desc");

  // Inline "Opportunity" editing (the MIS-move explanation, shown under the address)
  const [editingOppId, setEditingOppId] = useState(null);
  const [oppDraft, setOppDraft] = useState("");
  const [savingOpp, setSavingOpp] = useState(false);
  const [hoverDescId, setHoverDescId] = useState(null);  // expand the clamped Apt description on hover

  const startEditOpp = (item) => {
    setEditingOppId(item.sale_listing_id);
    setOppDraft(item.property?.mis_opportunity || "");
  };
  const saveOpp = async (item) => {
    setSavingOpp(true);
    try {
      await authFetch(`/sale-reviews/${item.sale_listing_id}/opportunity`, {
        method: "PATCH",
        body: JSON.stringify({ opportunity: oppDraft }),
      });
      setListings((prev) => prev.map((it) =>
        it.sale_listing_id === item.sale_listing_id
          ? { ...it, property: { ...it.property, mis_opportunity: oppDraft } }
          : it));
      setEditingOppId(null);
    } catch (e) {
      console.error("Save opportunity failed:", e);
    } finally {
      setSavingOpp(false);
    }
  };

  // Financial parameters (for recalculating cap rate, IRR, cash flow)
  const [mortgageType, setMortgageType] = useState("I/O");
  const [mortgageRate, setMortgageRate] = useState("");
  const [downPayment, setDownPayment] = useState("");
  const [appreciation] = useState("6");  // default 6%/yr for IRR (filter removed from UI)
  const [closingCostsConfig, setClosingCostsConfig] = useState(null);

  // Selection + email
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [emailSending, setEmailSending] = useState(false);
  const clientSearchRef = useRef(null);

  // Help modal
  const [helpTopic, setHelpTopic] = useState(null);

  // Comments popover
  const [commentsListingId, setCommentsListingId] = useState(null);
  const [commentsPos, setCommentsPos] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);

  useEffect(() => {
    loadNeighborhoods();
    loadMortgageConfig();
  }, []);

  const loadMortgageConfig = async () => {
    try {
      const config = await getMortgageConfig();
      const rates = config?.config_data?.rates || config?.rates || {};
      const dp = config?.config_data?.down_payment_percent || config?.down_payment_percent || 25;
      setDownPayment(String(dp));
      setMortgageRate(String(rates?.io?.["7_1_arm"] || 5.75));

      // Also load closing costs config
      const ccResp = await authFetch("/config/closing_costs");
      if (ccResp?.data) setClosingCostsConfig(ccResp.data);
    } catch (e) {
      console.error("Failed to load config:", e);
      setMortgageRate("5.75");
      setDownPayment("25");
    }
  };

  // Calculate financial metrics for a listing based on current parameters
  const calcMetrics = (item) => {
    const p = item.property;
    const price = p.price || 0;
    const rent = p.predicted_rent || 0;
    const monthlyTax = p.monthly_tax || 0;
    const monthlyHOA = p.monthly_hoa || 0;
    const carry = monthlyTax + monthlyHOA;
    const rate = parseFloat(mortgageRate) || 5.75;
    const dpPct = parseFloat(downPayment) || 25;
    const appRate = parseFloat(appreciation) || 6;

    if (!price || !rent) return { capRate: null, cashFlow: null, irr: null };

    // Cap rate: (rent - carry) * 12 / price * 100
    const capRate = ((rent - carry) * 12 / price) * 100;

    // Mortgage payment
    const loanAmount = price * (1 - dpPct / 100);
    let monthlyMortgage;
    if (mortgageType === "I/O") {
      monthlyMortgage = loanAmount * (rate / 100) / 12;
    } else {
      const r = rate / 100 / 12;
      const n = 30 * 12;
      monthlyMortgage = loanAmount * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    }

    // Cash flow
    const cashFlow = rent - carry - monthlyMortgage;

    // Closing costs (use full calculator if config loaded, else estimate)
    const dpAmount = price * (dpPct / 100);
    let netClosingCosts = Math.round(price * 0.025); // fallback estimate
    if (closingCostsConfig) {
      try {
        const buildingType = p.property_type || "Condo";
        const ccResult = calculateClosingCosts({
          askingPrice: price,
          buildingType,
          saleType: "Resale",
          financing: "Yes",
          downPaymentPercent: dpPct,
          config: closingCostsConfig,
        });
        netClosingCosts = ccResult.netClosingCosts;
      } catch (e) {
        // fallback to estimate
      }
    }

    // IRR (7yr)
    const totalOOP = dpAmount + netClosingCosts;

    const projection = generateCashFlowProjection({
      propertyPrice: price,
      monthlyRent: rent,
      monthlyCarryingCosts: carry,
      totalOutOfPocket: totalOOP,
      monthlyMortgagePayment: monthlyMortgage,
      mortgageRate: rate,
      mortgageTerm: 30,
      paymentType: mortgageType,
      downPaymentPercent: dpPct,
      rentGrowthRate: 5,
      propertyAppreciationRate: appRate,
      years: 7,
    });

    return {
      capRate: capRate,
      cashFlow: Math.round(cashFlow),
      irr: projection?.irr || null,
    };
  };

  useEffect(() => {
    if (neighborhoodsLoaded) {
      loadListings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNeighborhoods, neighborhoodsLoaded]);

  // Close comments popover on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setCommentsListingId(null);
      }
    };
    if (commentsListingId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [commentsListingId]);

  const HELP_TOPICS = {
    cap_rate: {
      title: "Cap Rate",
      content: `Cap Rate (Capitalization Rate) measures the return on a property based on rental income.

Formula: (Annual Net Income / Property Price) × 100

Where:
• Annual Net Income = (Monthly Rent - Monthly Tax - Monthly HOA) × 12
• Property Price = Listing price (or discounted price)

Example:
• Rent: $4,000/mo, Tax: $500/mo, HOA: $800/mo
• Net Income: ($4,000 - $500 - $800) × 12 = $32,400/yr
• Price: $800,000
• Cap Rate: $32,400 / $800,000 × 100 = 4.05%

A higher cap rate means better returns relative to price.`
    },
    cash_flow: {
      title: "Monthly Cash Flow",
      content: `Cash Flow is the monthly income after all expenses including mortgage.

Formula: Monthly Rent - Monthly Tax - Monthly HOA - Monthly Mortgage Payment

Mortgage Payment depends on:
• Mortgage Type: I/O (Interest Only) or P&I (Principal & Interest)
• Mortgage Rate: Annual interest rate
• Down Payment: Determines loan amount
• Loan Amount = Price × (1 - Down Payment %)

I/O Payment: Loan Amount × Rate / 12
P&I Payment: Standard amortization over 30 years

Positive cash flow = income exceeds expenses.
Negative cash flow = expenses exceed income (common in NYC).`
    },
    irr: {
      title: "IRR (7-Year Internal Rate of Return)",
      content: `IRR measures the total annualized return over a 7-year hold period, including cash flow and property appreciation.

Accounts for:
• Initial Investment: Down Payment + Closing Costs
• Annual Cash Flow: Rent - Taxes - HOA - Mortgage (growing at 5%/yr)
• Property Appreciation: Compounded annually (default 6%/yr)
• Exit: Sale at appreciated value minus 6.825% selling costs
• Final Year: Cash flow + equity from sale

Closing Costs include:
• Mansion Tax (price-dependent thresholds)
• Mortgage Recording Tax (Condo only, based on loan amount)
• Title Insurance (TIRSA tiered rates)
• Attorney, appraisal, and other fees

IRR is the discount rate that makes the NPV of all cash flows equal to zero.
Higher IRR = better risk-adjusted return.`
    },
    predicted_rent: {
      title: "Predicted Rent",
      content: `The ML-predicted monthly rent for this property if rented.

Calculated using an XGBoost model trained on comparable rental listings in the same neighborhood.

Model inputs:
• Bedrooms, Bathrooms, Square Footage
• Floor, Renovation Level, Views
• Building Amenities (doorman, elevator, etc.)
• Location Zone Score
• Days on Market

The prediction is used to calculate Cap Rate, Cash Flow, and IRR.`
    },
    ppsf: {
      title: "Price Per Square Foot (PPSF)",
      content: `Simple measure of property value relative to size.

Formula: Listing Price / Total Square Footage

Useful for comparing properties of different sizes within the same neighborhood.`
    },
  };

  const HelpIcon = ({ topic }) => (
    <span
      onClick={(e) => { e.stopPropagation(); setHelpTopic(topic); }}
      style={{
        display: "inline-block",
        width: 14, height: 14,
        borderRadius: "50%",
        border: "1px solid #bbb",
        fontSize: "10px",
        lineHeight: "14px",
        textAlign: "center",
        color: "#999",
        cursor: "pointer",
        marginLeft: "4px",
        verticalAlign: "middle",
      }}
      title="Click for details"
    >?</span>
  );

  // Client search with debounce
  useEffect(() => {
    if (clientSearch.length < 2) { setClientResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const data = await authFetch(`/households/search/clients?q=${encodeURIComponent(clientSearch)}`);
        setClientResults(data.clients || []);
      } catch (e) { setClientResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedListings.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedListings.map(i => i.sale_listing_id)));
    }
  };

  const addClient = (client) => {
    if (!selectedClients.find(c => c.id === client.id)) {
      setSelectedClients(prev => [...prev, client]);
    }
    setClientSearch("");
    setClientResults([]);
  };

  const removeClient = (id) => {
    setSelectedClients(prev => prev.filter(c => c.id !== id));
  };

  const handleCommentClick = (e, listingId) => {
    e.stopPropagation();
    if (commentsListingId === listingId) {
      setCommentsListingId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setCommentsPos({ top: rect.bottom + 4, left: Math.min(rect.left, window.innerWidth - 380) });
    setCommentsListingId(listingId);
  };

  const loadNeighborhoods = async () => {
    try {
      const data = await authFetch("/sale-reviews/neighborhoods");
      setNeighborhoods(data.neighborhoods || []);

      if (data.default_neighborhoods?.length > 0) {
        setDefaultNeighborhoods(data.default_neighborhoods);
        setSelectedNeighborhoods(data.default_neighborhoods);
      }
      setNeighborhoodsLoaded(true);
    } catch (error) {
      console.error("Failed to load neighborhoods:", error);
      setNeighborhoodsLoaded(true);
    }
  };

  const loadListings = async () => {
    setLoading(true);
    try {
      const neighborhoodParam = selectedNeighborhoods.length > 0
        ? `&neighborhood_ids=${selectedNeighborhoods.join(",")}`
        : "";
      const data = await authFetch(`/sale-reviews?limit=500&status=MIS${neighborhoodParam}`);
      const misItems = data.reviews || [];
      setListings(misItems);
      setTotalCount(misItems.length);
    } catch (error) {
      console.error("Failed to load MIS listings:", error);
    }
    setLoading(false);
  };

  // Client-side filtering
  const filteredListings = listings.filter((item) => {
    const p = item.property;
    if (!p) return false;

    if (searchAddress && !`${p.address} ${p.unit_number}`.toLowerCase().includes(searchAddress.toLowerCase())) return false;
    if (bedsMin && (p.bedrooms == null || p.bedrooms < parseInt(bedsMin))) return false;
    if (bedsMax && (p.bedrooms == null || p.bedrooms > parseInt(bedsMax))) return false;
    if (bathsMin && (p.bathrooms == null || p.bathrooms < parseFloat(bathsMin))) return false;
    if (bathsMax && (p.bathrooms == null || p.bathrooms > parseFloat(bathsMax))) return false;
    if (priceMin && (p.price == null || p.price < parseInt(priceMin))) return false;
    if (priceMax && (p.price == null || p.price > parseInt(priceMax))) return false;
    if (capRateMin && (p.cap_rate == null || p.cap_rate < parseFloat(capRateMin))) return false;
    if (capRateMax && (p.cap_rate == null || p.cap_rate > parseFloat(capRateMax))) return false;
    if (daysOnMarketMax && p.listed_at) {
      const dom = Math.floor((Date.now() - new Date(p.listed_at).getTime()) / (1000 * 60 * 60 * 24));
      if (dom > parseInt(daysOnMarketMax)) return false;
    }
    if (propertyType === "condo" && p.property_type?.toUpperCase() !== "CONDO") return false;
    if (propertyType === "coop" && !["COOP", "CONDP"].includes(p.property_type?.toUpperCase())) return false;

    return true;
  });

  // Sorting
  const sortedListings = [...filteredListings].sort((a, b) => {
    const ap = a.property;
    const bp = b.property;
    let av, bv;

    const am = calcMetrics(a);
    const bm = calcMetrics(b);

    switch (sortField) {
      case "date_added": av = a.updated_at || ""; bv = b.updated_at || ""; break;
      case "neighborhood": av = ap.neighborhood || ""; bv = bp.neighborhood || ""; break;
      case "address": av = ap.address || ""; bv = bp.address || ""; break;
      case "price": av = ap.price || 0; bv = bp.price || 0; break;
      case "predicted_rent": av = ap.predicted_rent || 0; bv = bp.predicted_rent || 0; break;
      case "cap_rate": av = am.capRate || 0; bv = bm.capRate || 0; break;
      case "cash_flow": av = am.cashFlow || 0; bv = bm.cashFlow || 0; break;
      case "irr": av = am.irr || 0; bv = bm.irr || 0; break;
      case "sqft": av = ap.sqft || 0; bv = bp.sqft || 0; break;
      case "ppsf":
        av = ap.price && ap.sqft ? ap.price / ap.sqft : 0;
        bv = bp.price && bp.sqft ? bp.price / bp.sqft : 0;
        break;
      default: av = 0; bv = 0;
    }

    if (typeof av === "string") {
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    return sortDir === "asc" ? av - bv : bv - av;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const SortHeader = ({ field, label, width }) => (
    <th
      onClick={() => handleSort(field)}
      style={{
        ...thStyle,
        width,
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {label} {sortField === field ? (sortDir === "asc" ? "^" : "v") : ""}
    </th>
  );

  const formatPrice = (price) => {
    if (!price) return "-";
    if (price >= 1000000) return `$${(price / 1000000).toFixed(2)}M`;
    return `$${(price / 1000).toFixed(0)}K`;
  };

  const inputStyle = {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    fontSize: "13px",
  };

  const labelStyle = {
    display: "block",
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "4px",
    color: "#495057",
  };

  const thStyle = {
    padding: "10px 12px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "600",
    color: "#495057",
    borderBottom: "2px solid #dee2e6",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "10px 12px",
    fontSize: "13px",
    borderBottom: "1px solid #f0f0f0",
    whiteSpace: "nowrap",
  };

  const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

  const condoOrCoop = (t) => {
    const u = (t || "").toUpperCase();
    if (u === "CONDO") return "Condo";
    if (u === "COOP" || u === "CONDP") return "Co-op";
    return t || "-";
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
        <Header showLogout={true} />
        <div style={{ padding: "20px", maxWidth: "1800px", margin: "0 auto", textAlign: "center", marginTop: "50px" }}>
          Loading MIS listings...
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Header showLogout={true} />
      <div style={{ padding: "20px", maxWidth: "1800px", margin: "0 auto" }}>

        {/* Filter + Financial Parameters Panel */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "16px",
          marginBottom: "20px",
          position: "sticky",
          top: 0,
          zIndex: 20,
          backgroundColor: "#f5f5f5",
          paddingTop: "8px",
        }}>
          {/* Left: Filters */}
          <div style={{
            padding: "16px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "#333", marginBottom: "12px" }}>Filters</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <div style={{ gridColumn: "span 2" }}>
                <NeighborhoodSelect
                  neighborhoods={neighborhoods}
                  selected={selectedNeighborhoods}
                  onChange={setSelectedNeighborhoods}
                  defaults={defaultNeighborhoods}
                  placeholder="All Neighborhoods"
                />
              </div>

              <div>
                <label style={labelStyle}>Address</label>
                <input type="text" placeholder="Search..." value={searchAddress} onChange={(e) => setSearchAddress(e.target.value)} style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>Property Type</label>
                <select value={propertyType} onChange={(e) => setPropertyType(e.target.value)} style={{ ...inputStyle, backgroundColor: "white" }}>
                  <option value="all">All Properties</option>
                  <option value="condo">Condo</option>
                  <option value="coop">Co-op</option>
                </select>
              </div>

              <div>
                <label style={labelStyle}>Bedrooms</label>
                <div style={{ display: "flex", gap: "6px" }}>
                  <input type="number" placeholder="Min" value={bedsMin} onChange={(e) => setBedsMin(e.target.value)} style={inputStyle} />
                  <input type="number" placeholder="Max" value={bedsMax} onChange={(e) => setBedsMax(e.target.value)} style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Bathrooms</label>
                <div style={{ display: "flex", gap: "6px" }}>
                  <input type="number" step="0.5" placeholder="Min" value={bathsMin} onChange={(e) => setBathsMin(e.target.value)} style={inputStyle} />
                  <input type="number" step="0.5" placeholder="Max" value={bathsMax} onChange={(e) => setBathsMax(e.target.value)} style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Price ($)</label>
                <div style={{ display: "flex", gap: "6px" }}>
                  <CurrencyInput placeholder="Min" value={priceMin} onChange={setPriceMin} style={inputStyle} />
                  <CurrencyInput placeholder="Max" value={priceMax} onChange={setPriceMax} style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Cap Rate (%)</label>
                <div style={{ display: "flex", gap: "6px" }}>
                  <input type="number" step="0.1" placeholder="Min" value={capRateMin} onChange={(e) => setCapRateMin(e.target.value)} style={inputStyle} />
                  <input type="number" step="0.1" placeholder="Max" value={capRateMax} onChange={(e) => setCapRateMax(e.target.value)} style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Days on Market</label>
                <input type="number" placeholder="Max days" value={daysOnMarketMax} onChange={(e) => setDaysOnMarketMax(e.target.value)} style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Right: Financial Parameters */}
          <div style={{
            padding: "16px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
          }}>
            <div style={{ fontSize: "13px", fontWeight: "600", color: "#333", marginBottom: "12px" }}>Financial Assumptions</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <div>
                <label style={labelStyle}>Mortgage Type</label>
                <select value={mortgageType} onChange={(e) => setMortgageType(e.target.value)} style={{ ...inputStyle, backgroundColor: "white" }}>
                  <option value="I/O">Interest Only</option>
                  <option value="P&I">Principal & Interest</option>
                </select>
              </div>

              <div>
                <label style={labelStyle}>Mortgage Rate (%)</label>
                <input type="number" step="0.125" value={mortgageRate} onChange={(e) => setMortgageRate(e.target.value)} style={inputStyle} />
              </div>

              <div>
                <label style={labelStyle}>Down Payment (%)</label>
                <input type="number" step="5" value={downPayment} onChange={(e) => setDownPayment(e.target.value)} style={inputStyle} />
              </div>

            </div>
          </div>
        </div>

        {/* Results count + Action bar */}
        <div style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "14px", color: "#666" }}>
            {sortedListings.length} MIS listing{sortedListings.length !== 1 ? "s" : ""}
            {filteredListings.length !== listings.length ? ` (filtered from ${listings.length})` : ""}
          </span>
          {selectedIds.size > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontSize: "13px", color: "#1976d2", fontWeight: "600" }}>
                {selectedIds.size} selected
              </span>
              <button
                onClick={() => setShowEmailModal(true)}
                style={{
                  padding: "6px 16px",
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "13px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                Send Email
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          overflow: "auto",
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#fafafa" }}>
                <th style={{ ...thStyle, width: "36px" }}>
                  <input
                    type="checkbox"
                    checked={selectedIds.size > 0 && selectedIds.size === sortedListings.length}
                    onChange={toggleSelectAll}
                    style={{ cursor: "pointer" }}
                  />
                </th>
                <SortHeader field="date_added" label="Date Added" />
                <SortHeader field="neighborhood" label="Neighborhood" />
                <SortHeader field="address" label="Address" />
                <th style={thStyle}>Unit</th>
                <th style={thStyle}>Type</th>
                <SortHeader field="price" label="Price" />
                <th style={thStyle}>Analysis</th>
                <th style={thStyle} onClick={() => handleSort("predicted_rent")}><span style={{ cursor: "pointer" }}>Pred. Rent {sortField === "predicted_rent" ? (sortDir === "asc" ? "^" : "v") : ""}</span><HelpIcon topic="predicted_rent" /></th>
                <th style={thStyle} onClick={() => handleSort("cap_rate")}><span style={{ cursor: "pointer" }}>Cap Rate {sortField === "cap_rate" ? (sortDir === "asc" ? "^" : "v") : ""}</span><HelpIcon topic="cap_rate" /></th>
                <th style={thStyle} onClick={() => handleSort("cash_flow")}><span style={{ cursor: "pointer" }}>Cash Flow {sortField === "cash_flow" ? (sortDir === "asc" ? "^" : "v") : ""}</span><HelpIcon topic="cash_flow" /></th>
                <th style={thStyle} onClick={() => handleSort("irr")}><span style={{ cursor: "pointer" }}>IRR (7Yr) {sortField === "irr" ? (sortDir === "asc" ? "^" : "v") : ""}</span><HelpIcon topic="irr" /></th>
                <th style={thStyle}>Bed/Bath</th>
                <SortHeader field="sqft" label="SF" />
                <th style={thStyle} onClick={() => handleSort("ppsf")}><span style={{ cursor: "pointer" }}>PPSF {sortField === "ppsf" ? (sortDir === "asc" ? "^" : "v") : ""}</span><HelpIcon topic="ppsf" /></th>
                <th style={{ ...thStyle, width: "40px" }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedListings.map((item) => {
                const p = item.property;
                const ppsf = p.price && p.sqft ? Math.round(p.price / p.sqft) : null;
                const dateAdded = item.updated_at ? new Date(item.updated_at).toLocaleDateString() : "-";
                const metrics = calcMetrics(item);
                return (
                  <Fragment key={item.id}>
                  <tr
                    style={{ transition: "background-color 0.15s" }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f5f5f5"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ""}
                  >
                    <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(item.sale_listing_id)}
                        onChange={() => toggleSelect(item.sale_listing_id)}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                    <td style={tdStyle}>{dateAdded}</td>
                    <td style={tdStyle}>{p.neighborhood || "-"}</td>
                    <td style={tdStyle}>
                      {p.listing_url ? (
                        <a
                          href={p.listing_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontWeight: "600", color: "#1976d2", textDecoration: "none" }}
                        >
                          {(p.address || "").trim()}
                        </a>
                      ) : (
                        <span style={{ fontWeight: "600", color: "#333" }}>{(p.address || "").trim()}</span>
                      )}
                    </td>
                    <td style={tdStyle}>{p.unit_number || "-"}</td>
                    <td style={tdStyle}>{condoOrCoop(p.property_type)}</td>
                    <td style={{
                      ...tdStyle,
                      fontWeight: "600",
                      color: p.price_change < 0 ? "#2e7d32" : p.price_change > 0 ? "#d32f2f" : "#000",
                    }}>
                      {formatPrice(p.price)}
                      {p.price_change ? (
                        <span style={{ fontSize: "11px", fontWeight: "400", marginLeft: "4px" }}>
                          {p.price_change < 0 ? "" : "+"}{p.price_change_pct ? `${p.price_change_pct.toFixed(1)}%` : ""}
                        </span>
                      ) : null}
                    </td>
                    <td style={tdStyle}>
                      <a
                        href={`/predict/${item.sale_listing_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: "#1976d2", fontSize: "12px" }}
                      >
                        View
                      </a>
                    </td>
                    <td style={tdStyle}>
                      {p.ai_predicted_rent != null
                        ? `$${p.ai_predicted_rent.toLocaleString()}`
                        : (p.predicted_rent != null ? `$${p.predicted_rent.toLocaleString()}` : "-")}
                      {p.ai_predicted_rent != null && p.predicted_rent != null && (
                        <div title="ML-predicted rent" style={{ fontSize: "11px", fontWeight: 600, color: "#1976d2" }}>
                          ML: ${p.predicted_rent.toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: "600", color: metrics.capRate >= 5 ? "#2e7d32" : metrics.capRate >= 4 ? "#ed6c02" : "#d32f2f" }}>
                      {metrics.capRate != null ? `${metrics.capRate.toFixed(2)}%` : "-"}
                    </td>
                    <td style={{ ...tdStyle, color: metrics.cashFlow > 0 ? "#2e7d32" : metrics.cashFlow < 0 ? "#d32f2f" : "#666" }}>
                      {metrics.cashFlow != null ? `$${metrics.cashFlow.toLocaleString()}` : "-"}
                    </td>
                    <td style={tdStyle}>{metrics.irr != null ? `${metrics.irr.toFixed(1)}%` : "-"}</td>
                    <td style={tdStyle}>
                      {p.bedrooms ?? "-"} / {p.bathrooms ?? "-"}
                    </td>
                    <td style={tdStyle}>{p.sqft ? p.sqft.toLocaleString() : "-"}</td>
                    <td style={tdStyle}>{ppsf ? `$${ppsf.toLocaleString()}` : "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <span
                        onClick={(e) => handleCommentClick(e, item.sale_listing_id)}
                        style={{
                          cursor: "pointer",
                          fontSize: "16px",
                          opacity: 0.5,
                          transition: "opacity 0.15s",
                        }}
                        onMouseEnter={(e) => e.target.style.opacity = 1}
                        onMouseLeave={(e) => e.target.style.opacity = 0.5}
                        title="Comments"
                      >
                        💬
                      </span>
                    </td>
                  </tr>
                  {(p.mis_opportunity || p.apt_description || editingOppId === item.sale_listing_id) && (
                    <tr>
                      <td colSpan={16} style={{ padding: "2px 12px 12px 46px", borderBottom: "1px solid #eee", background: "#fafbfc", whiteSpace: "normal" }}>
                        <div style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
                          {/* Apt description FIRST — clamp to 3 lines, hover to expand */}
                          <div style={{ flex: 1.4, fontSize: "12px", lineHeight: 1.45, color: "#444" }}>
                            {p.apt_description ? (
                              <div
                                onMouseEnter={() => setHoverDescId(item.id)}
                                onMouseLeave={() => setHoverDescId(null)}
                                style={hoverDescId === item.id ? { cursor: "default" } : {
                                  display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                                  overflow: "hidden", cursor: "pointer",
                                }}
                                title="Hover to expand">
                                <span style={{ fontWeight: 700, color: "#333" }}>Apt: </span>{stripHtml(p.apt_description)}
                              </div>
                            ) : null}
                          </div>
                          {/* Opportunity — editable */}
                          <div style={{ flex: 1, fontSize: "12px", lineHeight: 1.45 }}>
                            <span style={{ fontWeight: 700, color: "#b26a00" }}>Opportunity: </span>
                            {editingOppId === item.sale_listing_id ? (
                              <div onClick={(e) => e.stopPropagation()}>
                                <textarea value={oppDraft} onChange={(e) => setOppDraft(e.target.value)} rows={3} autoFocus
                                  style={{ width: "100%", fontSize: "12px", padding: "4px", border: "1px solid #ddd", borderRadius: "4px", boxSizing: "border-box" }} />
                                <div style={{ display: "flex", gap: "6px", marginTop: "3px" }}>
                                  <button onClick={() => saveOpp(item)} disabled={savingOpp}
                                    style={{ fontSize: "11px", padding: "3px 10px", border: "none", borderRadius: "3px", background: "#1976d2", color: "#fff", cursor: "pointer" }}>{savingOpp ? "Saving…" : "Save"}</button>
                                  <button onClick={() => setEditingOppId(null)}
                                    style={{ fontSize: "11px", padding: "3px 10px", border: "1px solid #ccc", borderRadius: "3px", background: "#fff", cursor: "pointer" }}>Cancel</button>
                                </div>
                              </div>
                            ) : (
                              <span onClick={(e) => { e.stopPropagation(); startEditOpp(item); }}
                                style={{ cursor: "pointer", color: p.mis_opportunity ? "#333" : "#999", fontStyle: p.mis_opportunity ? "normal" : "italic" }}
                                title="Click to edit">{p.mis_opportunity || "Add opportunity…"}</span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
              {sortedListings.length === 0 && (
                <tr>
                  <td colSpan={16} style={{ ...tdStyle, textAlign: "center", color: "#999", padding: "40px" }}>
                    No MIS listings found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Comments Popover */}
        {commentsListingId && (
          <div
            ref={popoverRef}
            style={{
              position: "fixed",
              top: commentsPos.top,
              left: commentsPos.left,
              width: "360px",
              maxHeight: "400px",
              overflowY: "auto",
              backgroundColor: "#fff",
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              border: "1px solid #e0e0e0",
              padding: "16px",
              zIndex: 1000,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
              <span style={{ fontSize: "14px", fontWeight: "600" }}>Comments</span>
              <span
                onClick={() => setCommentsListingId(null)}
                style={{ cursor: "pointer", fontSize: "18px", color: "#999", lineHeight: 1 }}
              >
                x
              </span>
            </div>
            <ListingComments listingId={commentsListingId} />
          </div>
        )}
        {/* Help Modal */}
        {helpTopic && HELP_TOPICS[helpTopic] && (
          <div
            style={{
              position: "fixed",
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
            onClick={() => setHelpTopic(null)}
          >
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                padding: "24px",
                maxWidth: "500px",
                width: "90%",
                maxHeight: "80vh",
                overflowY: "auto",
                boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h3 style={{ margin: 0, fontSize: "18px", color: "#333" }}>{HELP_TOPICS[helpTopic].title}</h3>
                <span
                  onClick={() => setHelpTopic(null)}
                  style={{ cursor: "pointer", fontSize: "20px", color: "#999", lineHeight: 1 }}
                >x</span>
              </div>
              <pre style={{
                fontSize: "13px",
                color: "#444",
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
                fontFamily: "inherit",
                margin: 0,
              }}>
                {HELP_TOPICS[helpTopic].content}
              </pre>
            </div>
          </div>
        )}
        {/* Email Modal */}
        {showEmailModal && (
          <div
            style={{
              position: "fixed",
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
            onClick={() => setShowEmailModal(false)}
          >
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                padding: "24px",
                maxWidth: "500px",
                width: "90%",
                boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h3 style={{ margin: 0, fontSize: "18px", color: "#333" }}>
                  Send {selectedIds.size} listing{selectedIds.size !== 1 ? "s" : ""} to client
                </h3>
                <span
                  onClick={() => setShowEmailModal(false)}
                  style={{ cursor: "pointer", fontSize: "20px", color: "#999", lineHeight: 1 }}
                >x</span>
              </div>

              {/* Selected listings preview */}
              <div style={{ marginBottom: "16px", maxHeight: "120px", overflowY: "auto" }}>
                {sortedListings.filter(i => selectedIds.has(i.sale_listing_id)).map(item => (
                  <div key={item.id} style={{ fontSize: "12px", color: "#666", padding: "2px 0" }}>
                    {(item.property.address || "").trim()} #{item.property.unit_number} — {formatPrice(item.property.price)}
                  </div>
                ))}
              </div>

              {/* Client search */}
              <div style={{ marginBottom: "16px", position: "relative" }}>
                <label style={labelStyle}>Send to (search by name or email)</label>
                <input
                  ref={clientSearchRef}
                  type="text"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Start typing client name or email..."
                  style={inputStyle}
                  autoFocus
                />
                {/* Autocomplete dropdown */}
                {clientResults.length > 0 && (
                  <div style={{
                    position: "absolute",
                    top: "100%",
                    left: 0, right: 0,
                    backgroundColor: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    zIndex: 10,
                    maxHeight: "200px",
                    overflowY: "auto",
                  }}>
                    {clientResults.map(c => (
                      <div
                        key={c.id}
                        onClick={() => addClient(c)}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          fontSize: "13px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#f5f5f5"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = ""}
                      >
                        <div style={{ fontWeight: "600" }}>{c.first_name} {c.last_name}</div>
                        <div style={{ fontSize: "11px", color: "#999" }}>{c.email}{c.household ? ` — ${c.household}` : ""}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected clients */}
              {selectedClients.length > 0 && (
                <div style={{ marginBottom: "16px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {selectedClients.map(c => (
                    <span
                      key={c.id}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "4px 10px",
                        backgroundColor: "#e3f2fd",
                        borderRadius: "16px",
                        fontSize: "12px",
                        color: "#1976d2",
                      }}
                    >
                      {c.first_name} {c.last_name}
                      <span
                        onClick={() => removeClient(c.id)}
                        style={{ cursor: "pointer", fontWeight: "bold", marginLeft: "2px" }}
                      >x</span>
                    </span>
                  ))}
                </div>
              )}

              {/* Send button */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                <button
                  onClick={() => setShowEmailModal(false)}
                  style={{
                    padding: "8px 20px",
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setEmailSending(true);
                    try {
                      const resp = await authFetch("/mis/send-email", {
                        method: "POST",
                        body: JSON.stringify({
                          listing_ids: Array.from(selectedIds),
                          client_emails: selectedClients.map(c => c.email),
                        }),
                      });
                      alert(`Email sent to ${resp.sent} recipient${resp.sent !== 1 ? "s" : ""}${resp.failed ? ` (${resp.failed} failed)` : ""}`);
                      setShowEmailModal(false);
                      setSelectedIds(new Set());
                      setSelectedClients([]);
                    } catch (e) {
                      alert("Failed to send email: " + (e.message || e));
                    }
                    setEmailSending(false);
                  }}
                  disabled={selectedClients.length === 0 || emailSending}
                  style={{
                    padding: "8px 20px",
                    backgroundColor: selectedClients.length > 0 ? "#1976d2" : "#ccc",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "13px",
                    cursor: selectedClients.length > 0 ? "pointer" : "default",
                  }}
                >
                  {emailSending ? "Sending..." : "Send Email"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
